import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { find } from 'lodash';
import {
    Dialog, DialogContent, makeStyles,
    Checkbox, FormControlLabel,
} from '@material-ui/core';
import { Form, Col } from 'react-bootstrap';
import Select from 'react-select';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';
import InputNumber from 'components/widgets/InputNumber';
import { ProductType } from 'utils/enum/DealEnum';
import { isValidField, isValidSchema } from 'utils/schema/utils';
import { ProductRowSchema } from 'utils/schema/deal/Product';
import ButtonStyles from 'styles/theme/Button';

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        padding: theme.spacing(2),
    },
    formGroup: {
        marginBottom: theme.spacing(2),
    },
    label: {
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.text.minsk,
        marginBottom: theme.spacing(0.5),
    },
    checkboxContainer: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(1),
    },
    ...ButtonStyles.getStyle(theme),
}));

const initialProduct = {
    type: '',
    vendor: { vendorId: null, vendorName: '' },
    product: { productId: null, productName: '' },
    price: 0,
    cost: 0,
    term: null,
    policyNumber: '',
    addToSellingPrice: false,
    dealProductId: null,
};

const ProductModal = ({
    open,
    onClose,
    onSave,
    product,
    products,
    getCurrentCompany,
    getCurrentPlan,
    isEditing,
}) => {
    const classes = useStyles();
    const [formData, setFormData] = useState(initialProduct);

    useEffect(() => {
        if (product) {
            setFormData(product);
        } else {
            setFormData(initialProduct);
        }
    }, [product, open]);

    const limitGap = products.filter((x) => x.type === ProductType.GAP).length > 0;
    const limitVsc = products.filter((x) => x.type === ProductType.VSC).length > 0;
    const limitProducts = products.filter((x) => x.type === ProductType.PRODUCT).length === 8;

    let typeOptions = [
        { value: 'VSC', label: 'VSC' },
        { value: 'PRODUCT', label: 'Products' },
        { value: 'GAP', label: 'Gap' },
    ];

    if (limitGap && !isEditing) typeOptions = typeOptions.filter((x) => x.value !== ProductType.GAP);
    if (limitVsc && !isEditing) typeOptions = typeOptions.filter((x) => x.value !== ProductType.VSC);
    if (limitProducts && !isEditing) typeOptions = typeOptions.filter((x) => x.value !== ProductType.PRODUCT);

    const typeValue = formData.type ? { label: formData.type, value: formData.type } : null;
    const optionVendor = getCurrentCompany(formData.type);
    const vendorValue = find(optionVendor, { value: formData.vendor?.vendorId });
    const optionPlan = getCurrentPlan(formData.type, vendorValue);
    const optionValue = find(optionPlan, { value: formData.product?.productId });

    const isValidData = isValidSchema(ProductRowSchema, formData);
    const { errors, isValid } = isValidData;

    const handleChangeType = (value) => {
        setFormData({
            ...formData,
            type: value,
            vendor: { vendorId: null, vendorName: '' },
            product: { productId: null, productName: '' },
        });
    };

    const handleChangeCompany = (vendorId) => {
        const selectedVendor = find(optionVendor, { value: vendorId });
        setFormData({
            ...formData,
            vendor: {
                vendorId,
                vendorName: selectedVendor?.label || '',
            },
            product: { productId: null, productName: '' },
        });
    };

    const handleChangePlan = (productId) => {
        const currentOptionPlan = getCurrentPlan(formData.type, vendorValue);
        const selectedPlan = find(currentOptionPlan, { value: productId });
        setFormData({
            ...formData,
            product: {
                productId,
                productName: selectedPlan?.label || '',
            },
            price: selectedPlan?.price || formData.price,
            cost: selectedPlan?.cost || formData.cost,
        });
    };

    const handleChange = (field, value) => {
        setFormData({
            ...formData,
            [field]: value,
        });
    };

    const handleSave = () => {
        onSave(formData);
        onClose();
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            aria-labelledby="product-modal-title"
        >
            <DialogAppBar
                title={isEditing ? 'Edit Product' : 'Add Product'}
                onClose={onClose}
                iconSize="sm"
                titleVariant="h4"
            />
            <DialogContent className={classes.dialogContent}>
                <Form>
                    <Form.Group className={classes.formGroup}>
                        <Form.Label className={classes.label}>Type</Form.Label>
                        <Select
                            className={clsx('select-bootstrap', 'select-sm', isValidField(errors, 'type') ? 'invalid-field' : '')}
                            value={typeValue}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            options={typeOptions}
                            placeholder="Select Type"
                            onChange={(e) => handleChangeType(e.value)}
                        />
                    </Form.Group>

                    <Form.Group className={classes.formGroup}>
                        <Form.Label className={classes.label}>Company</Form.Label>
                        <Select
                            className={clsx('select-bootstrap', 'select-sm', isValidField(errors, 'vendor.vendorId') ? 'invalid-field' : '')}
                            value={vendorValue || null}
                            options={optionVendor}
                            placeholder="Select Company"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            onChange={(e) => handleChangeCompany(e.value)}
                        />
                    </Form.Group>

                    <Form.Group className={classes.formGroup}>
                        <Form.Label className={classes.label}>Plan</Form.Label>
                        <Select
                            className={clsx('select-bootstrap', 'select-sm', isValidField(errors, 'product.productId') ? 'invalid-field' : '')}
                            value={optionValue || null}
                            options={optionPlan}
                            placeholder="Select Plan"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            onChange={(e) => handleChangePlan(e.value)}
                        />
                    </Form.Group>

                    <Form.Row>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Price</Form.Label>
                            <InputNumber
                                className={isValidField(errors, 'price') ? 'invalid-field' : ''}
                                value={formData.price}
                                onChange={(value) => handleChange('price', value)}
                                placeholder="0.00"
                                thousandSeparator
                                showCurrency
                                size="sm"
                            />
                        </Form.Group>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Cost</Form.Label>
                            <InputNumber
                                className={isValidField(errors, 'cost') ? 'invalid-field' : ''}
                                value={formData.cost}
                                onChange={(value) => handleChange('cost', value)}
                                placeholder="0.00"
                                thousandSeparator
                                showCurrency
                                size="sm"
                            />
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Term</Form.Label>
                            <InputNumber
                                value={formData.term || ''}
                                onChange={(value) => handleChange('term', value)}
                                placeholder="0"
                                thousandSeparator
                                decimalScale={0}
                                size="sm"
                            />
                        </Form.Group>
                        <Form.Group as={Col} className={classes.formGroup}>
                            <Form.Label className={classes.label}>Policy</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Policy Number"
                                maxLength="50"
                                value={formData.policyNumber || ''}
                                onChange={(e) => handleChange('policyNumber', e.target.value)}
                                size="sm"
                            />
                        </Form.Group>
                    </Form.Row>

                    <div className={classes.checkboxContainer}>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={formData.addToSellingPrice}
                                    color="primary"
                                    onChange={(e) => handleChange('addToSellingPrice', e.target.checked)}
                                />
                            )}
                            label="Line 1 sale"
                        />
                    </div>
                </Form>
            </DialogContent>
            <DialogActions
                titlePrimary={isEditing ? 'Update' : 'Add'}
                titleSecondary="Cancel"
                onClickPrimary={handleSave}
                onClickSecondary={onClose}
                disablePrimaryButton={!isValid}
            />
        </Dialog>
    );
};

ProductModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    product: PropTypes.object,
    products: PropTypes.array.isRequired,
    getCurrentCompany: PropTypes.func.isRequired,
    getCurrentPlan: PropTypes.func.isRequired,
    isEditing: PropTypes.bool,
};

ProductModal.defaultProps = {
    product: null,
    isEditing: false,
};

export default ProductModal;
